import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { IFormProfileExt } from 'legacy/lut/classes/models/IFormProfileExt'
import { OnboardingSources, OnbPageURLs } from 'legacy/lut/classes/models/onboarding/Onboarding'
import businessProfileStoreService from 'legacy/lut/classes/services/BusinessProfileStoreService'
import { EMarket } from 'legacy/lut/configs/geo'
import { checkEmailDomain } from 'legacy/lut/helpers/validateRules/checkEmailDomain'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useValidationRulesAndEmailList } from 'legacy/lut/hooks/useValidationRulesAndEmailList'
import { selectAssociativeLanguages, selectLanguages } from 'legacy/lut/selectors/app'
import { selectCountries } from 'legacy/lut/selectors/app'
import { IReferenceAssociativeLanguage } from 'legacy/lut/store/state'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers/validateFormData'
import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { Static } from 'core-components/types'
import { guid } from 'core/classes/helpers/GuidHelper'
import { IReference } from 'core/classes/models/IReference'
import { Crud } from 'core/types/Crud'
import { showError } from 'onboarding/classes/services/notification'
import { refreshProfilingProgressData } from 'onboarding/features/actions/refreshProgressData'
import { contactService } from 'onboarding/services/contactService'
import { EmailHelper } from 'onboarding/utils/email'

import { useGetPartnerData } from '../../onboarding/onbPartnerForm/hooks/useGetPartnerData'
import { SAVE_AS_DRAFT_CONTACT_FORM_KEY } from '../configs'
import { getInitialSaveAsDraftContact } from '../helpers/getInitialSaveAsDraftContact'
import { TDraft } from '../models'
import { FormFields } from '../saveAsDraftContact/FormFields'

import styles from './SaveAsDraftContact.module.scss'

interface IProps {
  draftContact: IFormProfile | DataModel
  onClose: (showWarning: boolean) => void
  createChangeContactRequest?: (item: IFormProfile) => void
  appSource?: string
  onApply?: () => void
  setContactSaved?: (contact: IFormProfile | IFormProfileExt | null) => void
}

export const SaveAsDraftContact = (props: IProps) => {
  const [draftContact] = useState<DataModel>(
    props.draftContact.clone ? props.draftContact.clone() : new DataModel(props.draftContact)
  )
  const dispatch = useAppDispatch()
  const [writeDraft, setWriteDraft] = useState<TDraft>(() => ({
    ...getInitialSaveAsDraftContact(),
    country: draftContact.get('CountryOfBusiness'),
  }))
  const [isSaving, setIsSaving] = useState(false)

  const associativeLanguages: IReferenceAssociativeLanguage[] = useSelector(
    selectAssociativeLanguages
  )
  const languages: IReference[] = useSelector(selectLanguages)
  const user = useSelector(selectUser)
  const countries = useSelector(selectCountries)

  const [{ formData, modifiedFields }] = useGetFormData<TDraft>(SAVE_AS_DRAFT_CONTACT_FORM_KEY)
  const trimmedEmail = formData.email ? formData.email.trim() : ''

  const preferredLanguages: IReference[] = useMemo(() => {
    if (!formData?.country) {
      return []
    }

    //looking for a list of preferred languages based on the selected country
    const preferredLanguagesSource = associativeLanguages.filter(
      (lang: IReferenceAssociativeLanguage) => lang.CountryCode === formData.country
    )

    return preferredLanguagesSource.map<IReference>((lang: IReference) => {
      const prettyLang = languages.find((el: IReference) => el.value === lang.value)
      return {
        name: (prettyLang && prettyLang.name) || lang.name,
        value: (prettyLang && prettyLang.value) || lang.value,
      }
    })
  }, [associativeLanguages, formData.country, languages])

  const partnerId = draftContact.get('PartnerID')
  const { validationRules, emailList } = useValidationRulesAndEmailList(
    partnerId,
    draftContact.get('CountryOfBusiness')
  )

  const { partnerData } = useGetPartnerData(partnerId)

  const isValidEmailDomain = async (value: string) => {
    if (EmailHelper.isHPDomain(value)) {
      return 'HP email cannot be used'
    }

    const companyUrl = partnerData?.url ?? ''
    const result = await checkEmailDomain({
      value,
      companyUrl,
      validationRules,
      emailList,
    })

    return result
  }

  useEffect(() => {
    if (preferredLanguages.length === 1) {
      setWriteDraft((prevState) => ({
        ...prevState,
        preferredLanguage: preferredLanguages[0].name || '',
      }))
    }
  }, [preferredLanguages])

  const draftContactMarket = draftContact.get('Market')

  const isBpRegistration = getUrlParam('type') === OnbPageURLs.BPRegistration

  const needPreferredLanguage =
    [EMarket.greaterAsia, EMarket.india].includes(draftContactMarket) || isBpRegistration

  const dropdownSources = useMemo(
    () => ({
      countries: new Crud({
        fetcher: new Static(countries.filter((item) => item.market === draftContactMarket)),
        keyField: 'value',
        defaultPaging: {
          page: 0,
          pageSize: 1000,
        },
      }),
      preferredLanguages: new Crud({
        fetcher: new Static(preferredLanguages),
        keyField: 'name',
      }),
    }),
    [countries, draftContactMarket, preferredLanguages]
  )

  const checkCanSave = async (partnerId: string, email: string) => {
    const errors = await validateFormData(formData, SAVE_AS_DRAFT_CONTACT_FORM_KEY)
    const isValid = !errors.length
    const isContactExist = isValid
      ? await contactService.checkExistenceByEmail(partnerId, email)
      : false

    return { isValid, isContactExist }
  }

  const onSave = async () => {
    try {
      setIsSaving(true)
      const localDraftContact = draftContact.clone()
      const { isValid, isContactExist } = await checkCanSave(
        localDraftContact.get('PartnerID'),
        trimmedEmail
      )

      if (isValid && !isContactExist) {
        localDraftContact.set('FirstName', formData.firstName)
        localDraftContact.set('LastName', formData.lastName)
        localDraftContact.set('CountryOfBusiness', formData.country)
        localDraftContact.set('Email', trimmedEmail)
        localDraftContact.set('PreferredLanguage', formData.preferredLanguage)
        localDraftContact.set('ContactID', `new-${guid()}`)

        const onboardingRequestId = getUrlParam('rid') as string
        if (onboardingRequestId) {
          const parsedOnboardingRequestId = parseInt(onboardingRequestId)
          localDraftContact.set('OnboardingRequestId', parsedOnboardingRequestId)
        }

        const changedProfileExt = await businessProfileStoreService.changeProfileExt(
          localDraftContact,
          'Draft',
          props.appSource,
          user.email
        )
        props.setContactSaved && props.setContactSaved(changedProfileExt)
        props.createChangeContactRequest &&
          props.createChangeContactRequest(localDraftContact.rawData)
        props.onApply && props.onApply()
        const showWarning = props.appSource !== OnboardingSources.BPRegistration
        props.onClose(showWarning)

        dispatch(refreshProfilingProgressData())
      } else if (isContactExist) {
        showError(`Contact with email '${trimmedEmail}' already exists!`)
      }
    } catch (e: any) {
      showError(e.message)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(SAVE_AS_DRAFT_CONTACT_FORM_KEY))
    }
  }, [])

  return (
    <div className={styles.root}>
      <h4>Save as draft</h4>

      <div className={styles.formFieldsWrapper}>
        <FormFields
          country={writeDraft.country}
          firstName={writeDraft.firstName}
          lastName={writeDraft.lastName}
          email={writeDraft.email}
          preferredLanguage={writeDraft.preferredLanguage}
          needPreferredLanguage={needPreferredLanguage}
          dropdownSources={dropdownSources}
          isValidEmailDomain={isValidEmailDomain}
        />
      </div>

      <Tooltip
        className={styles.saveButtonTooltip}
        tipContent={() => <span>No changes to save</span>}
        disabled={Boolean(modifiedFields.length)}
      >
        <Button disabled={!modifiedFields.length} onClick={onSave} loading={isSaving}>
          Save
        </Button>
      </Tooltip>
    </div>
  )
}
